// UPPDRAG-15 — pure helpers for the court-sponsor flow.
//
// Mirrors the league-side sponsor pattern (PR-16.3) but with a
// different storage-path scheme (per-court instead of per-league)
// and an active-window filter that excludes time-limited
// sponsorships outside their date range.
//
// File-validation helpers (size + MIME) are reused from
// `sponsorUploadHelpers.ts` — they're already generic.

import type { ValidationOk } from "./sponsorUploadHelpers";

/**
 * Storage object key for a court-sponsor logo.
 *
 *   courts/<courtId>/sponsor_<sponsorId>.<ext>
 *
 * The leading "logos/" comes from `supabase.storage.from("logos")` —
 * this helper returns just the object key inside that bucket. The
 * sponsorId is part of the path so re-uploading the same court's
 * sponsor logo replaces only that sponsor's file (admin can have
 * multiple sponsors per court).
 */
export function courtSponsorStoragePath(
  courtId: string,
  sponsorId: string,
  ext: ValidationOk["ext"],
): string {
  return `courts/${courtId}/sponsor_${sponsorId}.${ext}`;
}

/**
 * Returns true if the sponsor should be visible right now given the
 * optional active_from/active_to date range.
 *
 *   active_from   active_to    behaviour
 *   -----------   ---------    ---------
 *   null          null         always active (default)
 *   2026-01-01    null         active from 2026-01-01 onward
 *   null          2026-12-31   active until 2026-12-31
 *   2026-01-01    2026-12-31   active during 2026 only
 *
 * Date comparison uses lexicographic order of yyyy-MM-dd strings,
 * which matches chronological order. This keeps the function pure
 * (no Date construction needed for comparison) and timezone-safe
 * (date columns are stored as `date`, not `timestamptz`).
 *
 * The `today` parameter defaults to the current date in the local
 * timezone (matching the admin's perception). Pass an explicit
 * Date for testing or for org-timezone-aware contexts.
 */
export function isCourtSponsorActive(
  activeFrom: string | null | undefined,
  activeTo: string | null | undefined,
  today: Date = new Date(),
): boolean {
  // Format today as yyyy-MM-dd in local timezone for lexicographic compare.
  // Avoid date-fns dependency in this pure helper — keep it tiny + testable.
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const todayStr = `${yyyy}-${mm}-${dd}`;

  if (activeFrom && todayStr < activeFrom) return false;
  if (activeTo && todayStr > activeTo) return false;
  return true;
}

/**
 * Convenience: filter an array of sponsors down to those active
 * today, sorted by display_order asc (NULL → 0). Used by the player-
 * side BookingGrid to decide which logos to render in the court
 * header stack.
 */
export interface CourtSponsorRow {
  id: string;
  court_id: string;
  sponsor_name: string | null;
  logo_url: string;
  click_url: string | null;
  active_from: string | null;
  active_to: string | null;
  display_order: number | null;
}

export function activeSponsorsSorted(
  sponsors: CourtSponsorRow[],
  today: Date = new Date(),
): CourtSponsorRow[] {
  return sponsors
    .filter((s) => isCourtSponsorActive(s.active_from, s.active_to, today))
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
}
